import { composeFilters } from "@/shared/components/ResourceView/Filters/composeFilters";
import {
  ACTIVITY_WINDOW_MAX_MS,
  type ResolvedWindow,
  type TimeRange,
  type TimeWindow,
} from "@/shared/domain/actions";
import type { Filter } from "@/shared/domain/query/filters";
import type { Condition } from "@/types";

const CREATED = "created";

/**
 * The activity panel's window, from the list's "created" filter.
 *
 * Keyed on the filter rather than on the bounds it expands to, so "last 24 hours" is one window
 * however many times it is resolved. A bound the filter does not give is now (upper) or as far
 * back as the activity API allows (lower); a lower bound older than that is cut there and the
 * window says so. Without a lower bound, the window is fitted to the activity found
 * ({@link TimeWindow.fitToActivity}).
 */
export function activityWindowFor(filter: Filter | undefined): TimeWindow {
  return {
    key: filter ?? null,
    resolve: (now) => resolveCreatedWindow(filter, now),
    fitToActivity: boundsOf(filter).start === undefined,
  };
}

function boundsOf(filter: Filter | undefined): Partial<TimeRange> {
  return createdBounds(composeFilters([], filter ? [filter] : [], "AND"));
}

function resolveCreatedWindow(
  filter: Filter | undefined,
  now: Date,
): ResolvedWindow {
  const bounds = boundsOf(filter);
  // A preset's upper bound is in the future: the end of its minute, day, or month.
  const end = new Date(
    Math.min(bounds.end?.getTime() ?? now.getTime(), now.getTime()),
  );
  const floor = end.getTime() - ACTIVITY_WINDOW_MAX_MS;
  const wanted = bounds.start?.getTime() ?? floor;
  return {
    start: new Date(Math.max(wanted, floor)),
    end,
    truncated: wanted < floor,
  };
}

function createdBounds(condition: Condition | undefined): Partial<TimeRange> {
  if (!condition) {
    return {};
  }
  if ("conditions" in condition) {
    return condition.conditions.reduce<Partial<TimeRange>>(
      (acc, inner) => ({ ...acc, ...createdBounds(inner) }),
      {},
    );
  }
  if (condition.field !== CREATED || typeof condition.value !== "string") {
    return {};
  }
  switch (condition.comparator) {
    case "GREATER_THAN":
    case "GREATER_THAN_OR_EQUAL":
      return { start: new Date(condition.value) };
    case "LESS_THAN":
    case "LESS_THAN_OR_EQUAL":
      return { end: new Date(condition.value) };
    default:
      return {};
  }
}
